import { useEffect, useMemo } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { PencilEdit02Icon, Add01Icon } from "@hugeicons/core-free-icons"
import { useAuth } from "@clerk/react"
import { useCompositorStore } from "@/lib/stores/compositor-store"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { createAssetsApi } from "@/lib/api/assets"
import type { CompositorConfig, CompositorLayer } from "@/lib/types/compositor"
import { DEFAULT_TRANSFORM, DEFAULT_TEXT_STYLE } from "@/lib/types/compositor"

const TEXT_MODULES = ["text_input", "llm_text", "text_builder"]

function isAssetRef(v: unknown): v is { assetId: string } {
  return typeof v === "object" && v !== null && "assetId" in v && typeof (v as { assetId: unknown }).assetId === "string"
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

async function renderCompositorPreview(
  compositorConfig: Partial<CompositorConfig>,
  connections: Record<string, { text?: string; imageUrl?: string }>,
  signal: AbortSignal,
): Promise<string> {
  const cw = compositorConfig.canvas?.width ?? 1920
  const ch = compositorConfig.canvas?.height ?? 1080
  const targetW = 480
  const scale = targetW / cw
  const targetH = Math.round(ch * scale)

  const canvas = document.createElement("canvas")
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext("2d")!

  ctx.fillStyle = "#1a1a1a"
  ctx.fillRect(0, 0, targetW, targetH)

  for (const layer of compositorConfig.layers ?? []) {
    if (signal.aborted) break
    if (!layer.visible) continue
    const conn = connections[layer.id]

    ctx.save()
    ctx.globalAlpha = layer.opacity / 100

    if (layer.type === "background") {
      if (conn?.imageUrl) {
        try {
          const img = await loadImageEl(conn.imageUrl)
          if (!signal.aborted) ctx.drawImage(img, 0, 0, targetW, targetH)
        } catch {}
      } else {
        ctx.fillStyle = "#2a2a2a"
        ctx.fillRect(0, 0, targetW, targetH)
      }
    } else if (conn?.text !== undefined) {
      const ts = layer.textStyle ?? DEFAULT_TEXT_STYLE
      const fontSize = Math.max(1, Math.round(ts.fontSize * scale))
      const weightMap: Record<string, string> = {
        Thin: "100", Light: "300", Regular: "400", Medium: "500",
        "Semi Bold": "600", Bold: "700", "Extra Bold": "800",
      }
      const weight = weightMap[ts.fontWeight] ?? "400"
      const italic = ts.fontStyle?.toLowerCase() === "italic" ? "italic " : ""
      const fontSpec = `${italic}${weight} ${fontSize}px ${ts.fontFamily ?? "Inter"}, sans-serif`
      try { await document.fonts.load(fontSpec) } catch {}
      if (signal.aborted) break

      const x = Math.round(layer.transform.x * scale)
      const y = Math.round(layer.transform.y * scale)
      const w = Math.round(layer.transform.width * scale)
      const h = Math.round(layer.transform.height * scale)
      const rotation = layer.transform.rotation
      const fill = ts.fill ?? "ffffff"
      const r = parseInt(fill.slice(0, 2), 16)
      const g = parseInt(fill.slice(2, 4), 16)
      const b = parseInt(fill.slice(4, 6), 16)

      ctx.font = fontSpec
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${ts.fillOpacity / 100})`
      ctx.textAlign = ts.textAlign as CanvasTextAlign

      // Vertical alignment: position text within layer height
      let localY: number
      if (ts.verticalAlign === "center") {
        ctx.textBaseline = "middle"
        localY = h / 2
      } else if (ts.verticalAlign === "bottom") {
        ctx.textBaseline = "bottom"
        localY = h
      } else {
        ctx.textBaseline = "top"
        localY = 0
      }
      const localX = ts.textAlign === "center" ? w / 2 : ts.textAlign === "right" ? w : 0

      // Rotate around the layer's top-left origin, same as Konva
      ctx.save()
      ctx.translate(x, y)
      if (rotation !== 0) ctx.rotate((rotation * Math.PI) / 180)
      ctx.fillText(conn.text, localX, localY)
      ctx.restore()
    } else if (conn?.imageUrl) {
      try {
        const img = await loadImageEl(conn.imageUrl)
        if (!signal.aborted) {
          const x = Math.round(layer.transform.x * scale)
          const y = Math.round(layer.transform.y * scale)
          const w = Math.round(layer.transform.width * scale)
          const h = Math.round(layer.transform.height * scale)
          const rotation = layer.transform.rotation
          ctx.save()
          ctx.translate(x, y)
          if (rotation !== 0) ctx.rotate((rotation * Math.PI) / 180)
          ctx.drawImage(img, 0, 0, w, h)
          ctx.restore()
        }
      } catch {}
    }

    ctx.restore()
  }

  return canvas.toDataURL("image/jpeg", 0.85)
}

interface CompositorNodeContentProps {
  nodeId: string
  config: Record<string, unknown>
  onConfigChange: (nodeId: string, update: Record<string, unknown>) => void
}

export function CompositorNodeContent({ nodeId, config, onConfigChange }: CompositorNodeContentProps) {
  const openEditor = useCompositorStore((s) => s.openEditor)
  const pipelineNodes = usePipelineStore((s) => s.nodes)
  const pipelineEdges = usePipelineStore((s) => s.edges)
  const updateNodeConfig = usePipelineStore((s) => s.updateNodeConfig)
  const liveNode = pipelineNodes.find((n) => n.id === nodeId)
  const compositorConfig = (liveNode?.data.config ?? config) as Partial<CompositorConfig>

  const canvasWidth = compositorConfig.canvas?.width ?? 1920
  const canvasHeight = compositorConfig.canvas?.height ?? 1080
  const renderedOutput = compositorConfig.renderedOutput as string | undefined

  const { getToken } = useAuth()
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken])

  // Fingerprint of connected source data + config layout (excludes renderedOutput to avoid loops)
  const autoSnapshotKey = useMemo(() => {
    const sources: string[] = []
    for (const layer of compositorConfig.layers ?? []) {
      const edge = pipelineEdges.find((e) => e.target === nodeId && e.targetHandle === layer.id)
      if (!edge) continue
      const src = pipelineNodes.find((n) => n.id === edge.source)
      if (!src) continue
      if (TEXT_MODULES.includes(src.data.module as string)) {
        sources.push(`${layer.id}:t:${String(src.data.config.text ?? src.data.config.output ?? "")}`)
      } else if (src.data.module === "image_input") {
        const imgCfg = src.data.config.image
        sources.push(`${layer.id}:img:${isAssetRef(imgCfg) ? imgCfg.assetId : "data"}`)
      }
    }
    return JSON.stringify({ s: sources, canvas: compositorConfig.canvas, layers: compositorConfig.layers })
  }, [compositorConfig.canvas, compositorConfig.layers, pipelineEdges, pipelineNodes, nodeId])

  useEffect(() => {
    // Skip when the compositor editor is open for this node — it will snapshot on close
    const store = useCompositorStore.getState()
    if (store.isOpen && store.nodeId === nodeId) return

    const controller = new AbortController()
    const objectUrls: string[] = []

    async function run() {
      const connections: Record<string, { text?: string; imageUrl?: string }> = {}

      for (const layer of compositorConfig.layers ?? []) {
        if (controller.signal.aborted) return
        const edge = pipelineEdges.find((e) => e.target === nodeId && e.targetHandle === layer.id)
        if (!edge) continue
        const src = pipelineNodes.find((n) => n.id === edge.source)
        if (!src) continue

        if (TEXT_MODULES.includes(src.data.module as string)) {
          connections[layer.id] = { text: String(src.data.config.text ?? src.data.config.output ?? "") }
        } else if (src.data.module === "image_input") {
          const imgCfg = src.data.config.image
          if (isAssetRef(imgCfg)) {
            try {
              const blob = await assetsApi.fetchContent(imgCfg.assetId)
              if (!controller.signal.aborted) {
                const url = URL.createObjectURL(blob)
                objectUrls.push(url)
                connections[layer.id] = { imageUrl: url }
              }
            } catch {}
          } else {
            const dataUrl = (imgCfg as { dataUrl?: string } | undefined)?.dataUrl
            if (dataUrl) connections[layer.id] = { imageUrl: dataUrl }
          }
        }
      }

      if (controller.signal.aborted) return

      const dataUrl = await renderCompositorPreview(compositorConfig, connections, controller.signal)

      if (!controller.signal.aborted) {
        const node = usePipelineStore.getState().nodes.find((n) => n.id === nodeId)
        if (node) {
          updateNodeConfig(nodeId, {
            ...(node.data.config as Record<string, unknown>),
            renderedOutput: dataUrl,
          })
        }
      }
    }

    run().finally(() => { objectUrls.forEach((url) => URL.revokeObjectURL(url)) })
    return () => {
      controller.abort()
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSnapshotKey])

  function handleAddLayer() {
    const layers = compositorConfig.layers ?? []
    const nonBgCount = layers.filter((l) => l.type !== "background").length
    const newLayer: CompositorLayer = {
      id: `layer_${nonBgCount + 1}`,
      name: `Layer ${nonBgCount + 1}`,
      type: "image",
      visible: true,
      locked: false,
      transform: {
        ...DEFAULT_TRANSFORM,
        x: Math.round(canvasWidth * 0.1),
        y: Math.round(canvasHeight * 0.1),
        width: Math.round(canvasWidth * 0.8),
        height: Math.round(canvasHeight * 0.2),
      },
      opacity: 100,
      blendMode: "normal",
    }
    onConfigChange(nodeId, { ...config, layers: [...layers, newLayer] })
  }

  return (
    <div className="flex flex-col gap-2 px-1 pb-1">
      {/* Canvas preview */}
      <div
        className="relative rounded-md overflow-hidden bg-neutral-900 border border-border/30"
        style={{ aspectRatio: `${canvasWidth} / ${canvasHeight}` }}
      >
        {renderedOutput ? (
          <img
            src={renderedOutput}
            alt="canvas preview"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground/40">
              {compositorConfig.layers?.length ?? 0} layer{(compositorConfig.layers?.length ?? 0) !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={handleAddLayer}
          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <HugeiconsIcon icon={Add01Icon} size={12} />
          Add layer
        </button>
        <button
          onClick={() => openEditor(nodeId)}
          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-brand/10 text-brand hover:bg-brand/20 transition-colors font-medium"
        >
          <HugeiconsIcon icon={PencilEdit02Icon} size={12} />
          Edit
        </button>
      </div>
    </div>
  )
}
